import moment from 'moment'
import attendanceService from '@/services/attendance-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ATTENDANCE_PERMISSIONS } from '@/utils/attendance-permissions'
import {
  isPresentOrHalfDayLeaveType,
  requiresRegularizationTime,
} from '@/utils/attendance-leave-types'
export default {
  methods: {
    // ============================================
    // REGULARIZATION CASES LOADING
    // ============================================
    async loadRegularizationCases(empId = null) {
      try {
        const isShowingAllTeam = (this.viewMode === 'team' || this.viewMode === 'org') && !this.selectedTeamMember && !this.selectedTeamWiseEmployee
        const targetEmpId = isShowingAllTeam && this.viewMode === 'team'
          ? this.emp_id
          : (empId === null && isShowingAllTeam
            ? null
            : (empId || this.selectedTeamMember || this.selectedTeamWiseEmployee || this.currentViewEmpId || this.emp_id))

        let startDate = null
        let endDate = null
        if (this.activeFilters && this.activeFilters.startDate && this.activeFilters.endDate) {
          startDate = this.activeFilters.startDate
          endDate = this.activeFilters.endDate
        } else {
          startDate = moment().month(this.selectedMonth).year(this.selectedYear).startOf('month').format('YYYY-MM-DD')
          endDate = moment().month(this.selectedMonth).year(this.selectedYear).endOf('month').format('YYYY-MM-DD')
        }

        const apiRole = this.getApiRoleFromUserRole(this.userRole)
        const viewParam = (this.viewMode === 'team' || this.viewMode === 'org') ? this.viewMode : null
        const res = await attendanceService.getRegularizationCasesFromAPI(targetEmpId, apiRole, startDate, endDate, viewParam)

        if (!res || res.status === 'FAILED') {
          console.warn('Regularization API not available, setting empty array')
          this.regularizationCases = []
          return
        }

        if (res && res.status === 'OK') {
          const responseData = res.data || {}
          let allCases = []

          const isShowingAllTeamLocal = (this.viewMode === 'team' || this.viewMode === 'org') && !this.selectedTeamMember && !this.selectedTeamWiseEmployee

          if (!isShowingAllTeamLocal && responseData.self && Array.isArray(responseData.self)) {
            const mappedSelf = responseData.self.map(item => {
              const mapped = this.mapRegularizationCaseFromAPI(item)
              if (!mapped.emp_id) mapped.emp_id = targetEmpId
              return mapped
            })
            allCases = [...mappedSelf]
          }

          if ((this.viewMode === 'team' || this.viewMode === 'org') && (this.isAccountManager || this.isHR || this.isHRDirector || this.isAdmin || this.isMaster)) {
            if (responseData.team && Array.isArray(responseData.team)) {
              responseData.team.forEach(teamCase => {
                const mappedCase = this.mapRegularizationCaseFromAPI(teamCase)
                const exists = allCases.some(c =>
                  c.emp_id === mappedCase.emp_id &&
                  (c.datestring === mappedCase.datestring || c.date === mappedCase.date),
                )
                if (!exists) {
                  allCases.push(mappedCase)
                }
              })
            }
          }

          if (this.viewMode === 'org' && (this.isHR || this.isHRDirector)) {
            if (responseData.company && Array.isArray(responseData.company)) {
              let companyRegularizationsToAdd = responseData.company

              if (this.selectedAccountManager && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
                const teamEmpIds = this.teamWiseEmployees.map(emp => emp.emp_id || emp.id).filter(id => id)
                companyRegularizationsToAdd = responseData.company.filter(companyCase => {
                  const caseEmpId = companyCase.emp_id || companyCase.empId || companyCase.employee_id
                  return teamEmpIds.includes(caseEmpId)
                })
              }

              companyRegularizationsToAdd.forEach(companyCase => {
                const mappedCase = this.mapRegularizationCaseFromAPI(companyCase)
                const exists = allCases.some(c =>
                  c.emp_id === mappedCase.emp_id &&
                  (c.datestring === mappedCase.datestring || c.date === mappedCase.date),
                )
                if (!exists) {
                  allCases.push(mappedCase)
                }
              })
            }
          }

          allCases.forEach(discrepancy => {
            const empIdForTracking = discrepancy.emp_id || targetEmpId
            const dateStr = discrepancy.datestring || discrepancy.date
            if (dateStr) {
              const submissionKey = `${empIdForTracking}:${dateStr}`
              const recentSubmission = this.recentlySubmittedRegularizations.get(submissionKey)
              if (recentSubmission) {
                discrepancy.status = recentSubmission.status
                discrepancy.regularization_status = recentSubmission.regularization_status
                if (recentSubmission.id) {
                  discrepancy.id = recentSubmission.id
                  discrepancy.attendance_id = recentSubmission.id
                }
              }
            }
          })

          let filteredCases = allCases.filter(c => {
            const status = (c.status || c.regularization_status || c.current_status || '').toLowerCase()
            return status !== 'approved'
          })

          // Exclude today's local calendar day — day may still be in progress
          // (e.g. Missing Punch Out). Historical dates (yesterday and earlier) remain.
          const today = moment().format('YYYY-MM-DD')
          filteredCases = filteredCases.filter(caseItem => {
            const caseDate = caseItem.datestring || caseItem.date
            if (!caseDate) return true
            return moment(caseDate).format('YYYY-MM-DD') !== today
          })

          if (this.dateRangeFilter && Array.isArray(this.dateRangeFilter) && this.dateRangeFilter.length === 2) {
            const filterStartDate = moment(this.dateRangeFilter[0])
            const filterEndDate = moment(this.dateRangeFilter[1])
            filteredCases = filteredCases.filter(caseItem => {
              const caseDate = moment(caseItem.datestring || caseItem.date)
              return caseDate.isSameOrAfter(filterStartDate, 'day') && caseDate.isSameOrBefore(filterEndDate, 'day')
            })
          }

          const sortedCases = filteredCases.sort((a, b) => {
            const dateA = moment(a.datestring || a.date)
            const dateB = moment(b.datestring || b.date)
            return dateB.diff(dateA)
          })

          this.regularizationCases = sortedCases
          if (!this.dashboardData) this.dashboardData = {}
          this.$set(this.dashboardData, 'regularization_cases', sortedCases)
        } else {
          this.regularizationCases = []
          if (!this.dashboardData) this.dashboardData = {}
          this.$set(this.dashboardData, 'regularization_cases', [])
        }

        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
        this.recentlySubmittedRegularizations.forEach((value, key) => {
          if (value.timestamp < fiveMinutesAgo) {
            this.recentlySubmittedRegularizations.delete(key)
          }
        })
      } catch (err) {
        console.error('Error loading regularization cases:', err)
        this.regularizationCases = []
      }
    },

    // ============================================
    // REGULARIZATION MODAL
    // ============================================
    openRegularizationModal(item) {
      const dateStr = item.datestring || item.date
      const targetEmpId = item.emp_id || this.currentViewEmpId || this.emp_id

      let punchDataFromSource = null
      let biometricFromSource = null

      if (dateStr) {
        if (this.attendanceData && Array.isArray(this.attendanceData)) {
          const punchRecord = this.attendanceData.find(a =>
            a.date === dateStr && (a.emp_id === targetEmpId || !a.emp_id),
          )
          if (punchRecord && (punchRecord.intime || punchRecord.outtime)) {
            punchDataFromSource = { intime: punchRecord.intime, outtime: punchRecord.outtime, worktime: punchRecord.worktime }
          }
        }

        if (this.biometricData && Array.isArray(this.biometricData)) {
          const biometricRecord = this.biometricData.find(b =>
            (b.datestring === dateStr || b.date === dateStr) &&
            (b.empcode === targetEmpId || b.emp_id === targetEmpId || !b.empcode),
          )
          if (biometricRecord && (biometricRecord.intime || biometricRecord.outtime)) {
            biometricFromSource = { intime: biometricRecord.intime, outtime: biometricRecord.outtime, worktime: biometricRecord.worktime }
          }
        }
      }

      const enhancedItem = {
        ...item,
        punchData: item.punchData || punchDataFromSource,
        biometric: item.biometric || biometricFromSource,
        biometricRecord: item.biometricRecord || (biometricFromSource ? {
          intime: biometricFromSource.intime,
          outtime: biometricFromSource.outtime,
          worktime: biometricFromSource.worktime,
        } : null),
      }

      this.selectedRegularizationItem = enhancedItem

      const isValidTime = time => {
        if (!time) return false
        const timeStr = String(time).trim()
        return timeStr !== '' &&
               timeStr !== '0:00:00' &&
               timeStr !== '00:00:00' &&
               timeStr !== 'N/A' &&
               timeStr !== 'null' &&
               timeStr !== 'undefined'
      }

      const existingIntime = (enhancedItem.punchData && enhancedItem.punchData.intime) ||
                             enhancedItem.intime ||
                             (enhancedItem.biometricRecord && enhancedItem.biometricRecord.intime) ||
                             (enhancedItem.biometric && enhancedItem.biometric.intime) ||
                             (enhancedItem.attendance && enhancedItem.attendance.intime) ||
                             ''
      const existingOuttime = (enhancedItem.punchData && enhancedItem.punchData.outtime) ||
                              enhancedItem.outtime ||
                              (enhancedItem.biometricRecord && enhancedItem.biometricRecord.outtime) ||
                              (enhancedItem.biometric && enhancedItem.biometric.outtime) ||
                              (enhancedItem.attendance && enhancedItem.attendance.outtime) ||
                              ''

      this.regularizationForm.intime = isValidTime(existingIntime) ? existingIntime : ''
      this.regularizationForm.outtime = isValidTime(existingOuttime) ? existingOuttime : ''
      this.regularizationForm.comment = enhancedItem.remarks || enhancedItem.comment || ''
      this.regularizationForm.leave_type = enhancedItem.leave_type || null

      this.$refs.regularizationModal.show()
    },

    // ============================================
    // REGULARIZATION → LEAVE APPLICATION SYNC
    // ============================================
    shouldSyncLeaveApplication(leaveType) {
      if (!leaveType || leaveType === 'Regularization') return false
      if (typeof this.isPresentOrHalfDay === 'function' && this.isPresentOrHalfDay(leaveType)) return false
      return true
    },
    isInsufficientBalanceLeaveError(leaveRes) {
      if (!leaveRes) return false
      const messageText = (leaveRes.message || '').toLowerCase()
      if (messageText.includes('insufficient') || messageText.includes('not enough') || messageText.includes('balance')) {
        return true
      }
      const errors = leaveRes.errors || []
      return errors.some(error => {
        const text = (typeof error === 'string' ? error : (error.message || '')).toLowerCase()
        return text.includes('insufficient') || text.includes('not enough') || text.includes('balance')
      })
    },
    isFutureDeductionLeaveResponse(leaveRes) {
      if (!leaveRes || leaveRes.status !== 'OK') return false
      const data = leaveRes.data || {}
      if (data.future_deduction || data.deduct_from_future || data.deducted_from_future) return true
      const warnings = data.warnings || []
      return warnings.some(warning => {
        const code = (warning.code || warning.type || '').toLowerCase()
        const text = (warning.message || warning || '').toString().toLowerCase()
        return code.includes('future') || text.includes('future accrual') || text.includes('deducted from future')
      })
    },
    resolveLeaveTypeIdForApplication(selectedLeaveType) {
      if (!selectedLeaveType) return null

      const selectedLeaveTypeUpper = selectedLeaveType.toUpperCase()

      if (this.leaveTypesFromDB && this.leaveTypesFromDB.length > 0) {
        const foundType = this.leaveTypesFromDB.find(lt =>
          lt.code === selectedLeaveType ||
          lt.display_name === selectedLeaveType ||
          (lt.code && lt.code.toUpperCase() === selectedLeaveTypeUpper),
        )
        if (foundType) return foundType.id
      }

      if (this.filteredLeaveTypeOptions) {
        const foundOption = this.filteredLeaveTypeOptions.find(opt =>
          opt.value === selectedLeaveType || opt.text === selectedLeaveType,
        )
        if (foundOption && foundOption.id) return foundOption.id
      }

      if (this.leaveTypeOptions && this.leaveTypeOptions.length > 0) {
        const foundOption = this.leaveTypeOptions.find(opt =>
          opt.value === selectedLeaveType || opt.text === selectedLeaveType,
        )
        if (foundOption && foundOption.id) return foundOption.id
      }

      return null
    },
    buildLeaveApplicationPayloadFromRegularization({ targetEmpId, formattedDate, leaveType, comment, attendanceRecordId = null }) {
      const leaveTypeUpper = (leaveType || '').toUpperCase()
      let finalLeaveType = leaveType
      const isWFHType = leaveTypeUpper === 'WFH' || leaveTypeUpper === 'WORK FROM HOME'
      if (isWFHType) finalLeaveType = 'Work From Home'

      const PROBATION_NEVER_OVERRIDE = ['WFH', 'WORK FROM HOME', 'CO', 'CO_REQUEST',
        'COMPENSATORY', 'COMPENSATORY OFF', 'COMPENSATORY LEAVE', 'SL', 'SICK', 'SICK LEAVE',
        'MATERNITY', 'MATERNITY LEAVE', 'PATERNITY', 'PATERNITY LEAVE', 'UNPAID', 'UNPAID LEAVE']
      const PROBATION_OVERRIDE_ALLOWED = ['PL', 'CL', 'PRIVILEGE LEAVE', 'CASUAL LEAVE']

      const isProtectedType = PROBATION_NEVER_OVERRIDE.some(t => leaveTypeUpper === t || leaveTypeUpper.includes(t))
      const preserveLeaveType = isProtectedType ||
        (this.isOnProbation && !PROBATION_OVERRIDE_ALLOWED.some(t => leaveTypeUpper === t || leaveTypeUpper.includes(t)))

      let leaveComment = (comment || '').trim()
      if (attendanceRecordId) {
        leaveComment = leaveComment
          ? `${leaveComment} [Linked to regularization #${attendanceRecordId}]`
          : `[Attendance Regularization #${attendanceRecordId}]`
      }

      return {
        emp_id: targetEmpId,
        from_date: formattedDate,
        to_date: formattedDate,
        leave_type: finalLeaveType,
        leave_type_id: this.resolveLeaveTypeIdForApplication(leaveType),
        comment: leaveComment,
        is_half_day: false,
        half_day_type: null,
        applied_by: targetEmpId !== this.emp_id ? this.emp_id : null,
        preserve_leave_type: preserveLeaveType || undefined,
        source: 'regularization',
        // Backend: when balance is insufficient, deduct from future leave accrual instead of rejecting.
        allow_future_deduction: true,
      }
    },
    async finalizeSyncedLeaveApplication(leaveRes, attendanceRecordId) {
      const leaveId = leaveRes.data?.id ||
        leaveRes.data?.leave_id ||
        (Array.isArray(leaveRes.data) && leaveRes.data[0] ? (leaveRes.data[0].id || leaveRes.data[0].leave_id) : null)

      if (leaveId && attendanceRecordId) {
        try {
          await attendanceService.update(attendanceRecordId, { leave_id: leaveId })
        } catch (linkErr) {
          console.warn('Could not link leave application to regularization record:', linkErr)
        }
      }

      const futureDeduction = this.isFutureDeductionLeaveResponse(leaveRes)

      if (leaveRes.data && leaveRes.data.warnings && leaveRes.data.warnings.length > 0) {
        leaveRes.data.warnings.forEach(warning => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: warning.message || warning,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          }, { timeout: 4000 })
        })
      }

      return { success: true, leaveId, futureDeduction, response: leaveRes }
    },
    async syncLeaveApplicationFromRegularization({ targetEmpId, formattedDate, leaveType, comment, attendanceRecordId = null }) {
      const leaveData = this.buildLeaveApplicationPayloadFromRegularization({
        targetEmpId,
        formattedDate,
        leaveType,
        comment,
        attendanceRecordId,
      })

      try {
        let leaveRes = await attendanceService.applyLeave(leaveData)

        if (leaveRes && leaveRes.status === 'OK') {
          return this.finalizeSyncedLeaveApplication(leaveRes, attendanceRecordId)
        }

        if (leaveRes && leaveRes.status === 'VALIDATION_ERROR') {
          const suggestions = leaveRes.suggestions || []
          const futureSuggestion = suggestions.find(s =>
            s.action === 'future_deduction' || s.action === 'deduct_from_future',
          )
          const shouldRetryWithFutureDeduction = (futureSuggestion || this.isInsufficientBalanceLeaveError(leaveRes)) &&
            !leaveData.allow_future_deduction

          if (shouldRetryWithFutureDeduction) {
            const retryData = { ...leaveData, allow_future_deduction: true }
            const retryRes = await attendanceService.applyLeave(retryData)
            if (retryRes && retryRes.status === 'OK') {
              return this.finalizeSyncedLeaveApplication(retryRes, attendanceRecordId)
            }
            leaveRes = retryRes || leaveRes
          }

          const errors = leaveRes.errors || []
          const errorMessage = errors.length > 0
            ? (errors[0].message || errors[0])
            : (leaveRes.message || 'Leave application validation failed')
          return {
            success: false,
            message: errorMessage,
            insufficientBalance: this.isInsufficientBalanceLeaveError(leaveRes),
            response: leaveRes,
          }
        }

        return {
          success: false,
          message: leaveRes?.message || 'Failed to create leave application',
          response: leaveRes,
        }
      } catch (err) {
        console.error('Error syncing leave application from regularization:', err)
        const errorMessage = err.response?.data?.message || err.message || 'Failed to create leave application'
        return { success: false, message: errorMessage, error: err }
      }
    },

    // ============================================
    // REGULARIZATION SUBMISSION
    // ============================================
    async handleRegularize() {
      if (!this.selectedRegularizationItem) return

      const leaveType = this.regularizationForm.leave_type
      const timesRequired = requiresRegularizationTime(leaveType, this.leaveTypesFromDB)
      const isPresentOrHalfDayType = isPresentOrHalfDayLeaveType(leaveType, this.leaveTypesFromDB)
      const isLeaveTypeSelected = !!leaveType

      let finalIntime = null
      let finalOuttime = null

      if (isPresentOrHalfDayType) {
        if (!this.regularizationForm.intime) {
          this.$toast({ component: ToastificationContent, props: { title: 'Please enter In Time', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          return
        }
        if (!this.regularizationForm.outtime) {
          this.$toast({ component: ToastificationContent, props: { title: 'Please enter Out Time', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          return
        }
        finalIntime = this.regularizationForm.intime
        finalOuttime = this.regularizationForm.outtime
      } else if (timesRequired) {
        const existingIntime = this.selectedRegularizationItem.intime || ''
        const existingOuttime = this.selectedRegularizationItem.outtime || ''
        const needsIntime = !existingIntime || existingIntime === '0:00:00' || existingIntime.trim() === ''
        const needsOuttime = !existingOuttime || existingOuttime === '0:00:00' || existingOuttime.trim() === ''

        if (needsIntime && !this.regularizationForm.intime) {
          this.$toast({ component: ToastificationContent, props: { title: 'Please enter In Time', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          return
        }
        if (needsOuttime && !this.regularizationForm.outtime) {
          this.$toast({ component: ToastificationContent, props: { title: 'Please enter Out Time', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          return
        }

        finalIntime = this.regularizationForm.intime && this.regularizationForm.intime.trim() !== ''
          ? this.regularizationForm.intime
          : existingIntime
        finalOuttime = this.regularizationForm.outtime && this.regularizationForm.outtime.trim() !== ''
          ? this.regularizationForm.outtime
          : existingOuttime

        if (!finalIntime || !finalOuttime) {
          this.$toast({ component: ToastificationContent, props: { title: 'Both In Time and Out Time are required', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          return
        }
      } else {
        // Leave types (Sick, Casual, WFH, etc.) — times optional
        finalIntime = this.regularizationForm.intime || null
        finalOuttime = this.regularizationForm.outtime || null
      }

      try {
        if (!this.selectedRegularizationItem) {
          this.$toast({ component: ToastificationContent, props: { title: 'No regularization item selected', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          return
        }

        const submittedDate = this.selectedRegularizationItem.datestring || this.selectedRegularizationItem.date
        if (!submittedDate) {
          this.$toast({ component: ToastificationContent, props: { title: 'Invalid date for regularization', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          return
        }

        const formattedDate = moment(submittedDate).format('YYYY-MM-DD')
        if (!formattedDate || formattedDate === 'Invalid date') {
          this.$toast({ component: ToastificationContent, props: { title: 'Invalid date format', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          return
        }

        const targetEmpId = this.selectedRegularizationItem.emp_id ||
          ((this.viewMode === 'team' && this.selectedTeamMember && this.canManageLeaves && this.selectedTeamMember !== this.emp_id)
            ? this.selectedTeamMember
            : this.emp_id)

        let attendanceStatus = 'present'
        let leaveCount = 0

        if (isPresentOrHalfDayType) {
          attendanceStatus = 'Present'
          if (leaveType === 'Full Day') {
            leaveCount = 0
          } else if (leaveType === '1st Half Day' || leaveType === '2nd Half Day') {
            leaveCount = 0.5
          }
        } else if (isLeaveTypeSelected) {
          attendanceStatus = 'leave'
          leaveCount = 1
        } else {
          attendanceStatus = 'present'
          leaveCount = 0
        }

        const worktimeResult = this.calculateWorkTime(finalIntime, finalOuttime)
        const calculatedWorktime = worktimeResult ? worktimeResult.worktime : null

        const regularizationData = {
          emp_id: targetEmpId,
          date: formattedDate,
          intime: finalIntime,
          outtime: finalOuttime,
          worktime: calculatedWorktime,
          attendance: attendanceStatus,
          leave_type: leaveType || (isLeaveTypeSelected ? null : 'Regularization'),
          remarks: this.regularizationForm.comment,
          source_type: 'REGULARIZATION',
          regularized_by: this.emp_id,
          count: 1,
          leave_count: leaveCount,
          status: 'Pending Manager',
          regularization_status: 'Pending Manager',
        }

        const res = await attendanceService.addPost(regularizationData)

        if (res && res.status === 'OK') {
          const submittedRecordId = res.data && res.data[0] ? res.data[0] : null
          const shouldSyncLeave = this.shouldSyncLeaveApplication(leaveType)
          let leaveSyncResult = null

          if (shouldSyncLeave) {
            leaveSyncResult = await this.syncLeaveApplicationFromRegularization({
              targetEmpId,
              formattedDate,
              leaveType,
              comment: this.regularizationForm.comment,
              attendanceRecordId: submittedRecordId,
            })

            if (!leaveSyncResult.success) {
              const isBalanceError = leaveSyncResult.insufficientBalance
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: isBalanceError
                    ? 'Regularization submitted, but leave could not be applied'
                    : 'Regularization submitted, but leave application failed',
                  text: isBalanceError
                    ? `${leaveSyncResult.message || 'Insufficient leave balance.'} Future accrual deduction was not accepted — please apply leave separately from the Leaves tab.`
                    : `${leaveSyncResult.message || 'Could not create leave application.'} Please apply leave separately from the Leaves tab.`,
                  icon: 'AlertTriangleIcon',
                  variant: 'warning',
                },
              }, { timeout: 6000 })
            } else if (leaveSyncResult.futureDeduction) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Leave application submitted',
                  text: 'Insufficient current balance — the leave will be deducted from future accrual when new leaves are credited.',
                  icon: 'InfoIcon',
                  variant: 'info',
                },
              }, { timeout: 5000 })
            }
          }

          let successMessage = targetEmpId !== this.emp_id
            ? `Regularization submitted successfully on behalf of ${this.getUserName(targetEmpId)}!`
            : 'Regularization request submitted!'
          if (shouldSyncLeave && leaveSyncResult && leaveSyncResult.success) {
            successMessage = leaveSyncResult.futureDeduction
              ? `${successMessage} Leave application submitted — balance will be deducted from future accrual.`
              : `${successMessage} Leave application also submitted.`
          }
          this.$toast({ component: ToastificationContent, props: { title: successMessage, icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
          this.$refs.regularizationModal.hide()

          const submissionKey = `${targetEmpId}:${submittedDate}`
          this.recentlySubmittedRegularizations.set(submissionKey, {
            id: submittedRecordId,
            status: 'Pending Manager',
            regularization_status: 'Pending Manager',
            timestamp: Date.now(),
            intime: regularizationData.intime,
            outtime: regularizationData.outtime,
            worktime: this.calculateWorkTime(regularizationData.intime, regularizationData.outtime)?.worktime || null,
            remarks: regularizationData.remarks,
            leave_type: regularizationData.leave_type,
            issue_type: 'Regularization Required',
          })

          if (this.regularizationCases && Array.isArray(this.regularizationCases)) {
            const caseToUpdate = this.regularizationCases.find(c =>
              (c.datestring === submittedDate || c.date === submittedDate) && c.emp_id === targetEmpId,
            )
            if (caseToUpdate) {
              caseToUpdate.status = 'Pending Manager'
              caseToUpdate.regularization_status = 'Pending Manager'
              if (submittedRecordId) {
                caseToUpdate.id = submittedRecordId
                caseToUpdate.attendance_id = submittedRecordId
              }
            }
          }

          try {
            const dateRes = await attendanceService.getAttendanceByDateRange(submittedDate, submittedDate, targetEmpId)
            if (dateRes && dateRes.status === 'OK' && dateRes.data) {
              const submittedRecord = Array.isArray(dateRes.data)
                ? dateRes.data.find(r => r.emp_id === targetEmpId && r.date === submittedDate)
                : (dateRes.data.emp_id === targetEmpId && dateRes.data.date === submittedDate ? dateRes.data : null)

              if (submittedRecord && (submittedRecord.id || submittedRecord.attendance_id)) {
                this.recentlySubmittedRegularizations.set(submissionKey, {
                  id: submittedRecord.id || submittedRecord.attendance_id || submittedRecordId,
                  status: submittedRecord.status || submittedRecord.regularization_status || 'Pending Manager',
                  regularization_status: submittedRecord.regularization_status || submittedRecord.status || 'Pending Manager',
                  timestamp: Date.now(),
                  intime: submittedRecord.intime || regularizationData.intime,
                  outtime: submittedRecord.outtime || regularizationData.outtime,
                  worktime: submittedRecord.worktime || this.calculateWorkTime(regularizationData.intime, regularizationData.outtime)?.worktime || null,
                  remarks: submittedRecord.remarks || regularizationData.remarks,
                  leave_type: submittedRecord.leave_type || regularizationData.leave_type,
                  issue_type: this.getIssueTypeFromRecord(submittedRecord),
                })
              }
            }
          } catch (err) {
            console.error('Error fetching submitted record:', err)
          }

          await new Promise(resolve => setTimeout(resolve, 500))
          await this.loadData()

          if (this.regularizationCases && Array.isArray(this.regularizationCases) && submittedRecordId) {
            const caseAfterReload = this.regularizationCases.find(c =>
              ((c.datestring === submittedDate || c.date === submittedDate) && c.emp_id === targetEmpId) ||
              (c.id === submittedRecordId || c.attendance_id === submittedRecordId),
            )
            if (caseAfterReload) {
              if (!caseAfterReload.status || caseAfterReload.status === 'Pending') {
                caseAfterReload.status = 'Pending Manager'
                caseAfterReload.regularization_status = 'Pending Manager'
                if (submittedRecordId) {
                  caseAfterReload.id = submittedRecordId
                  caseAfterReload.attendance_id = submittedRecordId
                }
              }
            }
          }
        } else {
          this.$toast({ component: ToastificationContent, props: { title: res?.message || 'Failed to submit regularization', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
        }
      } catch (err) {
        console.error('Error regularizing attendance:', err)
        this.$toast({ component: ToastificationContent, props: { title: 'An error occurred while regularizing attendance', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
      }
    },
    resetRegularizationForm() {
      this.selectedRegularizationItem = null
      this.regularizationForm = {
        intime: '',
        outtime: '',
        comment: '',
        leave_type: null,
      }
    },
    async loadPendingRegularizations() {
      if (!this.canManageLeaves) return

      try {
        const managerId = this.isAccountManager ? this.emp_id : null
        const res = await attendanceService.getPendingRegularizations(managerId)
        if (res && res.status === 'OK' && res.data) {
          this.pendingRegularizations = res.data || []
        } else {
          this.pendingRegularizations = []
        }
      } catch (err) {
        console.error('Error loading pending regularizations:', err)
        this.pendingRegularizations = []
      }
    },

    // ============================================
    // REGULARIZATION APPROVAL / REJECTION
    // ============================================
    openApproveRegularizationModal(regularization) {
      this.selectedRegularizationForApproval = regularization
      this.regularizationApprovalComment = ''
      this.$refs['modal-approve-regularization']?.show()
    },
    openRejectRegularizationModal(regularization) {
      this.selectedRegularizationForRejection = regularization
      this.regularizationRejectionReason = ''
      this.$refs['modal-reject-regularization']?.show()
    },
    async handleApproveRegularizationConfirm() {
      if (!this.selectedRegularizationForApproval) return

      if (!this.regularizationApprovalComment || !this.regularizationApprovalComment.trim()) {
        this.$toast({ component: ToastificationContent, props: { title: 'Comment Required', text: 'Please provide a comment for approval', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      const regularization = this.selectedRegularizationForApproval
      try {
        const canApproveFinal = this.canApproveRegularizationAllStages
        const canApproveManager = this.canApproveRegularizationManagerStage && !canApproveFinal
        const approverType = (this.userRole || 'EMPLOYEE').toUpperCase().replace(/\s+/g, '-')

        let statusToSet = ''
        if (canApproveFinal) {
          statusToSet = 'Approved'
        } else if (canApproveManager) {
          statusToSet = 'HR Pending'
        }

        const currentDateTime = moment().format('YYYY-MM-DD HH:mm:ss')
        const managerUpdateDt = canApproveManager ? currentDateTime : null
        const hrUpdateDt = canApproveFinal ? currentDateTime : null
        const hrId = canApproveFinal ? this.emp_id : null

        let comment = this.regularizationApprovalComment.trim()
        if (canApproveFinal && regularization.manager_comment) {
          const managerComment = regularization.manager_comment || regularization.manager_remarks || ''
          if (managerComment && comment) {
            comment = `${managerComment} | ${comment}`
          } else if (managerComment) {
            comment = managerComment
          }
        }

        const approvalData = {
          attendance_id: regularization.id || regularization.attendance_id,
          approver_id: this.emp_id,
          approver_type: approverType,
          comment,
          status: statusToSet,
          approved_by: this.emp_id,
          approvedAt: new Date().toISOString(),
          approver_comment: this.regularizationApprovalComment.trim(),
          hrId,
          managerUpdateDt,
          hrUpdateDt,
          leave_type: regularization.leave_type || null,
        }

        const timesRequired = requiresRegularizationTime(
          regularization.leave_type,
          this.leaveTypesFromDB,
        ) && String(regularization.attendance || '').toLowerCase() !== 'leave'

        const res = await attendanceService.approveRegularization(approvalData)
        if (res && res.status === 'OK') {
          const successMessage = canApproveManager
            ? 'Regularization approved by Manager. Sent to HR for final approval.'
            : `Regularization approved successfully for ${this.getUserName(regularization.emp_id)}!`
          this.$toast({ component: ToastificationContent, props: { title: successMessage, icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
          this.$refs['modal-approve-regularization']?.hide()
          this.selectedRegularizationForApproval = null
          this.regularizationApprovalComment = ''
          await this.loadData()
          await this.loadPendingRegularizations()
        } else {
          const errMsg = (res?.message || '').toLowerCase()
          const timeRelatedError = errMsg.includes('time') || errMsg.includes('punch')
          const title = (!timesRequired && timeRelatedError)
            ? 'Approval blocked by server'
            : (res?.message || 'Failed to approve regularization')
          const text = (!timesRequired && timeRelatedError)
            ? 'This leave-type regularization should not require punch times. Please contact HR if the issue persists.'
            : undefined
          this.$toast({
            component: ToastificationContent,
            props: {
              title,
              text,
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          }, { timeout: timeRelatedError && !timesRequired ? 5000 : 3000 })
        }
      } catch (err) {
        console.error('Error approving regularization:', err)
        this.$toast({ component: ToastificationContent, props: { title: 'An error occurred while approving regularization', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
      }
    },
    async rejectRegularization() {
      if (!this.selectedRegularizationForRejection) return
      if (!this.regularizationRejectionReason.trim()) {
        this.$toast({ component: ToastificationContent, props: { title: 'Please provide a reason for rejection', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      try {
        const canRejectFinal = this.canApproveRegularizationAllStages
        const canRejectManager = this.canApproveRegularizationManagerStage && !canRejectFinal
        const approverType = (this.userRole || 'EMPLOYEE').toUpperCase().replace(/\s+/g, '-')
        const status = canRejectFinal ? 'HR Reject' : 'Rejected By Manager'
        const currentDateTime = moment().format('YYYY-MM-DD HH:mm:ss')
        const managerUpdateDt = canRejectManager ? currentDateTime : null
        const hrUpdateDt = canRejectFinal ? currentDateTime : null
        const hrId = canRejectFinal ? this.emp_id : null

        let comment = this.regularizationRejectionReason.trim()
        if (canRejectFinal && this.selectedRegularizationForRejection.manager_comment) {
          const managerComment = this.selectedRegularizationForRejection.manager_comment || this.selectedRegularizationForRejection.manager_remarks || ''
          if (managerComment && comment) {
            comment = `${managerComment} | ${comment}`
          } else if (managerComment) {
            comment = managerComment
          }
        }

        const rejectionData = {
          attendance_id: this.selectedRegularizationForRejection.id || this.selectedRegularizationForRejection.attendance_id,
          approver_id: this.emp_id,
          approver_type: approverType,
          reason: comment,
          status,
          rejected_by: this.emp_id,
          rejectedAt: new Date().toISOString(),
          approver_comment: this.regularizationRejectionReason.trim(),
          hrId,
          managerUpdateDt,
          hrUpdateDt,
        }

        const res = await attendanceService.rejectRegularization(rejectionData)

        if (res && res.status === 'OK') {
          const successMessage = `Regularization rejected for ${this.getUserName(this.selectedRegularizationForRejection.emp_id)}`
          this.$toast({ component: ToastificationContent, props: { title: successMessage, icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
          this.$refs['modal-reject-regularization']?.hide()
          this.selectedRegularizationForRejection = null
          this.regularizationRejectionReason = ''
          await this.loadData()
          await this.loadPendingRegularizations()
        } else {
          this.$toast({ component: ToastificationContent, props: { title: res?.message || 'Failed to reject regularization', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
        }
      } catch (err) {
        console.error('Error rejecting regularization:', err)
        this.$toast({ component: ToastificationContent, props: { title: 'An error occurred while rejecting regularization', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
      }
    },
    scrollToRegularization() {
      document.getElementById('regularization-section')?.scrollIntoView({ behavior: 'smooth' })
    },
  },
}
